import React, { useEffect, useState } from 'react';

function MaintenanceRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maintenanceUnread, setMaintenanceUnread] = useState(0);
  const [lastViewedAt, setLastViewedAt] = useState(null);
  const [error, setError] = useState('');
  const [comments, setComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [commentSending, setCommentSending] = useState({});
  const [hideNewItems, setHideNewItems] = useState(localStorage.getItem('dismiss_newitems_landlord_maintenance') === '1');
  const [maintenanceLocked, setMaintenanceLocked] = useState(false);

  useEffect(() => {
    const fetchRequests = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const [countRes, requestsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/maintenance/unread-count`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/maintenance`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (countRes.ok) {
          const countData = await countRes.json().catch(() => ({}));
          setMaintenanceUnread(countData.count || 0);
          setLastViewedAt(countData.last_viewed_at || null);
        }

        if (requestsRes.status === 402) {
          setMaintenanceLocked(true);
          setRequests([]);
          setLoading(false);
          return;
        }
        const data = await requestsRes.json().catch(() => ({}));
        if (!requestsRes.ok) throw new Error(data.error || 'Failed to fetch maintenance requests');
        setRequests(data.requests || []);

        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/maintenance/mark-viewed`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        window.dispatchEvent(new Event('maintenance:viewed'));
        setMaintenanceUnread(0);
      } catch (err) {
        setError(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, []);

  useEffect(() => {
    const fetchAllComments = async () => {
      const token = localStorage.getItem('token');
      if (!token || requests.length === 0) return;
      const entries = await Promise.all(
        requests.map(async (req) => {
          try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/maintenance/${req.id}/comments`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json().catch(() => ({}));
            return [req.id, res.ok ? (data.comments || []) : []];
          } catch (err) {
            return [req.id, []];
          }
        })
      );
      const sorted = Object.fromEntries(entries.map(([id, list]) => [id, (list || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))]));
      setComments(sorted);
    };

    fetchAllComments();
  }, [requests]);

  const handleAddComment = async (requestId) => {
    const token = localStorage.getItem('token');
    const text = (commentText[requestId] || '').trim();
    if (!token || !text || commentSending[requestId]) return;
    setCommentSending(prev => ({ ...prev, [requestId]: true }));
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/maintenance/${requestId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ comment_text: text })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send comment');
      setComments(prev => ({
        ...prev,
        [requestId]: [data.comment, ...(prev[requestId] || [])]
      }));
      setCommentText(prev => ({ ...prev, [requestId]: '' }));
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setCommentSending(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const updateStatus = async (id, status) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/maintenance/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update status');
      setRequests(prev => prev.map(r => r.id === id ? data.request : r));
    } catch (err) {
      setError(err.message || 'Something went wrong');
    }
  };

  if (loading) return <main className="App-main">Loading maintenance requests...</main>;
  if (maintenanceLocked) return <main className="App-main"><p className="status-text">Upgrade to Pro to access maintenance requests.</p></main>;
  if (error) return <main className="App-main"><p className="error-message">{error}</p></main>;

  const getLastActivity = (req) => {
    const requestTime = req.updated_at || req.created_at;
    const commentTime = (comments[req.id] || [])[0]?.created_at;
    if (requestTime && commentTime) {
      return new Date(requestTime) > new Date(commentTime) ? requestTime : commentTime;
    }
    return requestTime || commentTime || null;
  };

  const latestRequests = [...requests]
    .sort((a, b) => new Date(getLastActivity(b) || 0) - new Date(getLastActivity(a) || 0))
    .slice(0, 3);

  return (
    <main className="App-main">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Operations</p>
          <h2>Maintenance Requests {maintenanceUnread > 0 && <span className="nav-badge">{maintenanceUnread}</span>}</h2>
          <p>Track issues, update status, and review tenant history.</p>
        </div>
      </div>

      {!hideNewItems && maintenanceUnread > 0 && latestRequests.length > 0 && (
        <div className="dashboard-section new-items-card" style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff' }}>
          <div className="section-header">
            <h3>New items</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="report-chip report-chip--muted">Latest activity</span>
              <button
                className="card-dismiss"
                onClick={() => { localStorage.setItem('dismiss_newitems_landlord_maintenance', '1'); setHideNewItems(true); }}
                aria-label="Dismiss"
              >
                Dismiss
              </button>
            </div>
          </div>
        {latestRequests.length === 0 ? (
          <p className="status-text">No new maintenance activity yet.</p>
        ) : (
          <div className="inquiry-list">
            {latestRequests.map((req) => {
              const lastActivity = getLastActivity(req);
              const isNew = lastViewedAt && lastActivity && new Date(lastActivity) > new Date(lastViewedAt);
              return (
                <div key={`latest-${req.id}`} className="inquiry-card">
                  <p><strong>{req.title}</strong></p>
                  <p className="status-text">{req.tenant_email} • {req.property_address}</p>
                  <p className="timestamp">{lastActivity ? new Date(lastActivity).toLocaleString() : '—'}</p>
                  <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                    <a className="secondary-button" href={`#request-${req.id}`}>View request</a>
                    {isNew && <span className="report-chip" style={{ marginLeft: '0.5rem' }}>New</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {requests.length === 0 ? (
        <p className="status-text">No maintenance requests yet.</p>
      ) : (
        <div className="maintenance-list">
          {requests.map((req) => {
            const history = requests.filter(r => r.tenant_id === req.tenant_id && r.property_id === req.property_id && r.id !== req.id).slice(0, 5);
            const createdAt = req.created_at ? new Date(req.created_at).toLocaleDateString() : '';
            return (
              <div key={req.id} id={`request-${req.id}`} className="maintenance-row">
                <div className="maintenance-summary">
                  <div className="maintenance-title">
                    <h3>{req.title}</h3>
                    <span className={`status-pill ${req.status}`}>{req.status}</span>
                  </div>
                  <div className="maintenance-meta">
                    <span><strong>Tenant:</strong> {req.tenant_email}</span>
                    <span><strong>Property:</strong> {req.property_address}</span>
                    {createdAt && <span><strong>Submitted:</strong> {createdAt}</span>}
                  </div>
                </div>

                <details className="maintenance-details">
                  <summary>View details</summary>
                  <div className="maintenance-details-body">
                    <p>{req.description}</p>
                    <div className="landlord-property-actions">
                      {req.active_tenant_id ? (
                        <a className="secondary-button" href={`/landlord/my-tenants/${req.active_tenant_id}`}>View Tenant</a>
                      ) : (
                        <button className="secondary-button" disabled>View Tenant</button>
                      )}
                      <button className="secondary-button" onClick={() => updateStatus(req.id, 'open')}>Open</button>
                      <button className="secondary-button" onClick={() => updateStatus(req.id, 'in_progress')}>In Progress</button>
                      <button className="secondary-button" onClick={() => updateStatus(req.id, 'resolved')}>Resolved</button>
                    </div>

                    {history.length > 0 && (
                      <div className="maintenance-history">
                        <p><strong>History</strong></p>
                        <ul>
                          {history.map(h => (
                            <li key={h.id}>{new Date(h.created_at).toLocaleDateString()} — {h.title} ({h.status})</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="lease-chat-thread" style={{ marginTop: '0.75rem' }}>
                      {(comments[req.id] || []).length === 0 ? (
                        <p className="status-text">No comments yet.</p>
                      ) : (
                        (comments[req.id] || []).map((comment) => {
                          const isUnread = lastViewedAt && new Date(comment.created_at) > new Date(lastViewedAt) && comment.user_type === 'tenant';
                          return (
                            <div key={comment.id} className={`lease-chat-message ${comment.user_type} ${isUnread ? 'unread' : ''}`}>
                              <p>{comment.comment_text}</p>
                              <span>{comment.user_type === 'landlord' ? 'Owner' : 'Tenant'}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                    <div className="lease-chat-compose">
                      <textarea
                        className="comment-box"
                        rows="2"
                        value={commentText[req.id] || ''}
                        onChange={(e) => setCommentText(prev => ({ ...prev, [req.id]: e.target.value }))}
                        placeholder="Add a comment..."
                      />
                      <button className="secondary-button" onClick={() => handleAddComment(req.id)} disabled={commentSending[req.id] || !(commentText[req.id] || '').trim()}>
                        {commentSending[req.id] ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>
                </details>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}

export default MaintenanceRequests;
